import { TransformNode, Vector3 } from '@babylonjs/core';
import type { Scene } from '@babylonjs/core';

import type { DiagramSummaryDto, NodeTypeDto } from '../generated/contracts';
import { toDrawableConnection, toDrawableNode } from '../ui/viewer/summaryToDrawable';
import { ConnectionRenderer } from './ConnectionRenderer';
import { ModelLibrary } from './ModelLibrary';
import { NodeMeshFactory } from './NodeMeshFactory';

/**
 * One diagram, rendered small enough to stand on a shelf.
 *
 * **A miniature is the diagram itself, not a picture of one** (SPEC-155). The node shapes,
 * silhouettes, model library and connection geometry all already exist, so rendering at a few
 * percent scale needs no thumbnail pipeline, no headless renderer, no cache to invalidate — and
 * can never be stale, because there is nothing to go stale. That is the whole argument for the
 * memory-palace picker being cheap rather than the expensive thing it looks like.
 *
 * Built from the same `DiagramSummaryDto` the desktop viewer reads, through the same
 * `summaryToDrawable` mapping, so a miniature and the viewer cannot disagree about what a diagram
 * looks like — they are running the identical geometry code on the identical payload.
 */
// spec: SPEC-155
// spec: SPEC-191
export class DiagramMiniature {
  /** Everything this miniature owns hangs off here; parent it wherever it should stand. */
  readonly root: TransformNode;

  private readonly content: TransformNode;

  private readonly factory: NodeMeshFactory;

  private readonly connections: ConnectionRenderer;

  private disposed = false;

  constructor(
    scene: Scene,
    parent: TransformNode,
    readonly diagramId: string,
    // Defaulted, matching NodeMeshFactory's own optional-parameter shape, rather than required —
    // a test can still build a miniature with nothing preloaded. Every real caller (LobbyView)
    // passes the room's own already-preloaded library instead of taking the default: this
    // constructor used to always take the default, which is the whole of SPEC-191's headset
    // report ("a bunch of boxes") — a miniature's own NodeMeshFactory had an empty ModelLibrary
    // with nothing in it and no way to ever get anything in it, so every model-backed node in
    // every diagram fell back to its procedural silhouette, and six of those types share the
    // plain box (CLAUDE.md, "Silhouettes.Box is shared by six catalogue types").
    models: ModelLibrary = new ModelLibrary(scene),
  ) {
    this.root = new TransformNode(`miniature-${diagramId}`, scene);
    this.root.parent = parent;

    // A second node inside the root, and the split is load-bearing: `content` carries the
    // fit-to-unit-box transform derived from the diagram's own bounds, while `root` carries
    // where the miniature stands and how big it is. Collapsing them into one would make placing
    // a miniature silently rescale its contents, so a diagram would change size depending on
    // which rank it happened to be in.
    this.content = new TransformNode(`miniature-content-${diagramId}`, scene);
    this.content.parent = this.root;

    this.factory = new NodeMeshFactory(scene, this.content, models);
    this.connections = new ConnectionRenderer(scene, this.content);
  }

  setCatalog(types: NodeTypeDto[]): void {
    this.factory.setCatalog(types);
  }

  /**
   * Builds the geometry and fits it into a box one unit across, centred on the miniature's own
   * origin.
   *
   * **Fitted from the diagram's own bounds rather than from the room template's** — unlike
   * `RoomFit`, which exists to put a canonical arrangement onto a particular floor (SPEC-131).
   * A miniature has no floor: it is an object standing in space, and what matters is that every
   * miniature is the same size regardless of how large or small the diagram inside it is, so a
   * shelf of them reads as a shelf rather than as a size chart.
   */
  build(summary: DiagramSummaryDto): void {
    if (this.disposed) {
      return;
    }

    this.clear();

    const centres = new Map<string, Vector3>();

    for (const node of summary.nodes) {
      const drawable = toDrawableNode(node);
      this.factory.create(drawable);
      centres.set(node.id, new Vector3(node.position.x, node.position.y, node.position.z));
    }

    for (const connection of summary.connections) {
      const from = centres.get(connection.fromNodeId);
      const to = centres.get(connection.toNodeId);

      // A connection whose endpoints are not both in the summary is skipped rather than drawn to
      // the origin. The summary is bounded (SPEC-157), so an edge leaving the included set is an
      // ordinary consequence of that bound, not a broken diagram.
      if (from === undefined || to === undefined) {
        continue;
      }

      this.connections.render(toDrawableConnection(connection), from, to);
    }

    this.fitToUnitBox(summary);
  }

  /** True once anything has been built into it — an empty diagram renders as an empty shelf slot
   *  rather than as nothing at all, which is why callers need to be able to ask. */
  get isEmpty(): boolean {
    return this.content.getChildMeshes(false).length === 0;
  }

  /** Where this miniature stands, and how big. Set from a `LobbySlot`. */
  place(x: number, y: number, z: number, scale: number): void {
    this.root.position.set(x, y, z);
    this.root.scaling.setAll(scale);
  }

  setEnabled(enabled: boolean): void {
    this.root.setEnabled(enabled);
  }

  clear(): void {
    this.connections.clear();

    for (const mesh of this.content.getChildMeshes(false)) {
      mesh.material?.dispose();
      mesh.dispose(true, false);
    }
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }

    this.disposed = true;
    this.clear();
    this.content.dispose();
    this.root.dispose();
  }

  /**
   * Scales and offsets `content` so the diagram's own extent becomes a unit box centred on the
   * miniature's origin.
   *
   * Computed from the summary's stored positions rather than from world bounding boxes,
   * deliberately: a world-bounds read would need `computeWorldMatrix(true)` on every mesh and
   * would be answering a question about geometry that has not been laid out yet, while the
   * positions are already exactly the numbers the fit is about.
   */
  private fitToUnitBox(summary: DiagramSummaryDto): void {
    if (summary.nodes.length === 0) {
      this.content.position.setAll(0);
      this.content.scaling.setAll(1);
      return;
    }

    let minX = Infinity;
    let minY = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let maxZ = -Infinity;

    for (const node of summary.nodes) {
      minX = Math.min(minX, node.position.x);
      minY = Math.min(minY, node.position.y);
      minZ = Math.min(minZ, node.position.z);
      maxX = Math.max(maxX, node.position.x);
      maxY = Math.max(maxY, node.position.y);
      maxZ = Math.max(maxZ, node.position.z);
    }

    const spanX = maxX - minX;
    const spanY = maxY - minY;
    const spanZ = maxZ - minZ;

    // The largest span decides the scale, so the diagram's proportions survive. Scaling each
    // axis to fill the box independently would stretch a wide, flat architecture into a cube and
    // make two genuinely different diagrams look alike.
    const largest = Math.max(spanX, spanY, spanZ);

    // A single node — or several stacked at one point — has no extent to divide by. Left at 1:1
    // rather than scaled by an epsilon, which would blow one shape up to fill the whole slot.
    const scale = largest > 0.001 ? 1 / largest : 1;

    this.content.scaling.setAll(scale);
    this.content.position.set(
      -((minX + maxX) / 2) * scale,
      -((minY + maxY) / 2) * scale,
      -((minZ + maxZ) / 2) * scale,
    );
  }
}
